import { FC, useContext, useEffect, useState } from 'react';
import { AgentThreadInput, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { AIAgent, AIAgentInputField, AIAgentInputFieldType } from '@nx-frontend/shared/types/ai-agents';
import { AgentInputFile } from './agent-inputs/file';
import { AgentInputText } from './agent-inputs/text';
import { ChatbotContext } from './chatbot-context';

// Define component props type for form components
type FormComponentProps = {
  field: AIAgentInputField;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onChange: (value: string | File) => void;
  showError?: boolean;
  errorText?: string;
  defaultValue?: unknown;
  cancelDefaultValue?: () => void;
};

// Create a generic form component type that adapts to each input type
const formComponents: {
  [K in AIAgentInputFieldType]: FC<FormComponentProps>;
} = {
  [AIAgentInputFieldType.TEXT]: AgentInputText as FC<FormComponentProps>,
  [AIAgentInputFieldType.FILE]: AgentInputFile as FC<FormComponentProps>,
};

interface AgentModeFormProps {
  onFieldsChange?: (fields: Record<string, string | File>) => void;
  inputsWithErrors?: { id: string; error: string }[];
  agent: AIAgent;
  defaults?: AgentThreadInput;
  cancelDefaultValue: (fieldId: string) => void;
}

export const AgentModeFormFields: FC<AgentModeFormProps> = ({
  onFieldsChange,
  inputsWithErrors,
  agent,
  defaults = {},
  cancelDefaultValue,
}) => {
  const [fieldsState, setFieldsState] = useState<Record<string, string | File>>({});
  const [showErrorsIn, setShowErrorsIn] = useState<{ id: string; error: string }[]>(inputsWithErrors ?? []);

  const aiContext = useContext(ChatbotContext);

  useEffect(() => {
    setShowErrorsIn(inputsWithErrors ?? []);
  }, [inputsWithErrors]);

  const onFieldChange = (field: AIAgentInputField, value: string | File) => {
    setFieldsState((prev) => ({
      ...prev,
      [field.id]: value,
    }));

    if (onFieldsChange) {
      onFieldsChange({
        ...fieldsState,
        [field.id]: value,
      });
    }

    setShowErrorsIn((prev) => prev.filter(({ id }) => id !== field.id));
  };

  if (!agent) {
    return <div>Agent not found (2)</div>;
  }

  return (
    <div>
      {agent.inputs.map((field) => {
        const Component = formComponents[field.type];
        if (!Component) {
          console.error('Unknown input type', field.type);
          return null;
        }

        return (
          <Component
            onChange={onFieldChange.bind(null, field)}
            key={field.id}
            field={field}
            themeStyle={aiContext.themeStyle as PortalStyleConfig}
            themeColors={aiContext.themeColors as ThemeColorSchemas}
            showError={showErrorsIn?.some(({ id }) => id === field.id)}
            errorText={showErrorsIn?.find(({ id }) => id === field.id)?.error}
            defaultValue={defaults[field.id] || undefined}
            cancelDefaultValue={() => cancelDefaultValue(field.id)}
          />
        );
      })}
    </div>
  );
};
